import { DateTime } from 'luxon'
import { PricedSKU, SKU } from './SKU'
import { VoucherSKUValue } from './VoucherSKUValue'

export class Voucher {

    constructor(
        public id: string,
        public displayName: string,
        public description: string | null,
        public imageId: string | null,
        public policy: string | null,
        public expiryMonths: number | null,
        public expiryDate: Date | null,
        public postalDeliveryFee: number | null,
        public skus: SKU[],
        public skuValues: VoucherSKUValue[]
    ) { }

    get isOnSale(): boolean {
        if (this.expiryDate && DateTime.fromJSDate(this.expiryDate, { zone: 'utc' }).toFormat('yyyy-MM-dd') < DateTime.local().toFormat('yyyy-MM-dd')) {
            return false
        }
        return this.skus.some(sku => sku.onSale && sku.price !== null)
    }

    get lowestPricedSKU(): PricedSKU | null {
        return this.skusSortedByPriceLowestFirst[0] ?? null
    }

    get lowestPricedAmount(): number | null {
        const sku = this.lowestPricedSKU
        return sku ? this.getAmountForSKU(sku.id) : null
    }

    get skusSortedByPriceLowestFirst(): PricedSKU[] {
        return this.skus.filter((sku): sku is PricedSKU => sku.onSale && sku.price !== null).sort((a, b) => a.price - b.price)
    }

    get postalDeliveryEnabled(): boolean {
        return this.postalDeliveryFee !== null
    }

    getSKUWithId(skuId: string): PricedSKU | null {
        return this.skus.find((sku): sku is PricedSKU => sku.id === skuId && sku.price !== null) || null
    }

    getValueForSKU(skuId: string): VoucherSKUValue | null {
        return this.skuValues.find(override => override.skuId === skuId) || null
    }

    hasSKUValue(skuId: string): boolean {
        return this.getValueForSKU(skuId) !== null
    }

    getAmountForSKU(skuId: string): number {
        const sku = this.getSKUWithId(skuId)
        if (!sku) {
            return 0
        }
        const value = this.getValueForSKU(skuId)
        return value ? value.amount : sku.price
    }

    getExpiryDateFromDate(date: Date): Date | null {
        if (this.expiryMonths === null && this.expiryDate === null) {
            return null
        }
        const monthsEndDate = this.expiryMonths !== null
            ? DateTime.fromJSDate(date)
            .plus({ months: this.expiryMonths })
            .toJSDate()
            : null
        if (monthsEndDate && this.expiryDate) {
            return this.expiryDate < monthsEndDate ? this.expiryDate : monthsEndDate
        }
        if (this.expiryDate) {
            return this.expiryDate
        }
        return monthsEndDate
    }
}
